import axios, { type AxiosResponse } from 'axios';
import { logger } from '@trading-utils/logger';

import { processBackendError } from '@/utils/error.utils';

import i18n from '@/i18n/config';

import { apiRoutes } from '@/config/apiRoutes';

import type { Company, Plant, CreatePlantPayload, CreateCompanyUserPayload, User } from '@/interfaces';

export const getCompanyDetails = async (companyId: number): Promise<Company> => {
  const url = `${apiRoutes.base}${apiRoutes.company.base}/${companyId}`;

  try {
    const response: AxiosResponse<Company> = await axios.get(url);

    logger.info(`Fetched company details for company ${companyId}`);

    return response.data;
  } catch (error) {
    logger.error('Error fetching company details', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorGetCompanyDetails'));
  }
};

export const createPlant = async (payload: CreatePlantPayload): Promise<Plant> => {
  const url = `${apiRoutes.base}${apiRoutes.plants.base}`;

  try {
    const response: AxiosResponse<Plant> = await axios.post(url, {
      identifier: payload.identifier,
      alias: payload.alias,
      usersIds: payload.users.map((user) => user.id),
      companyId: payload.companyId,
    });

    logger.info('Plant created successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error creating plant', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorCreatePlant'));
  }
};

export const updatePlant = async (id: number, payload: Partial<CreatePlantPayload>): Promise<Plant> => {
  const url = `${apiRoutes.base}${apiRoutes.plants.base}/${id}`;

  try {
    const response: AxiosResponse<Plant> = await axios.put(url, {
      identifier: payload.identifier,
      alias: payload.alias,
      usersIds: payload.users?.map((user) => user.id),
      companyId: payload.companyId,
    });

    logger.info('Plant updated successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error updating plant', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorUpdatePlant'));
  }
};

export const deletePlant = async (id: number): Promise<Plant> => {
  const url = `${apiRoutes.base}${apiRoutes.plants.base}/${id}`;

  try {
    const response: AxiosResponse<Plant> = await axios.delete(url);

    logger.info('Plant deleted successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error deleting plant', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorDeletePlant'));
  }
};

export const createCompanyUser = async (payload: CreateCompanyUserPayload): Promise<User> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}`;

  try {
    const response: AxiosResponse<User> = await axios.post(url, {
      name: payload.name,
      email: payload.email,
      role: payload.role,
      companyId: payload.companyId,
      plantIds: payload.plants.map((plant) => plant.id),
    });

    logger.info('User created successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error creating user', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorCreateCompanyUser'));
  }
};

export const updateCompanyUser = async (id: number, payload: Partial<CreateCompanyUserPayload>): Promise<User> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}/${id}`;

  try {
    const response: AxiosResponse<User> = await axios.put(url, {
      name: payload.name,
      role: payload.role,
      plantIds: payload.plants?.map((plant) => plant.id),
    });

    logger.info('User updated successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error updating user', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorUpdateCompanyUser'));
  }
};

export const deleteCompanyUser = async (id: number): Promise<User> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}/${id}`;

  try {
    const response: AxiosResponse<User> = await axios.delete(url);

    logger.info('User deleted successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error deleting user', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorDeleteCompanyUser'));
  }
};
